import React, { useState } from 'react';
import { GradientButton } from './style';
import { DEFAULT_ROUTES } from '@mais-retorno-front/constants/routes';
import { RedirectProDialog } from './components/redirect-pro-dialog';
import { Button } from '@core/design-system/button';
import { LPBaseURL, UTMContent } from '../utm-button/enum';
import { generateUTMLink } from '../utm-button/helper';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { useRouter } from 'next/router';

type RedirectToolButtonProps = any & {
  textVariant: 'navbar' | 'userCard' | 'subscriptionList';
  userIsPro?: boolean;
  userIsPremium?: boolean;
  showProRedirectConfirmation?: boolean;
  utmContent: UTMContent;
};

export const RedirectToolButton = ({
  textVariant,
  userIsPro = false,
  userIsPremium = false,
  showProRedirectConfirmation = true,
  utmContent,
  ...props
}: RedirectToolButtonProps) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const dropdownOpen = Boolean(anchorEl);
  const { pathname } = useRouter();

  const primeLink = generateUTMLink({
    utmContent,
    baseUrl: LPBaseURL.PRIME,
    pathname,
  });
  const proLink = generateUTMLink({
    utmContent,
    baseUrl: LPBaseURL.PRO,
    pathname,
  });

  const handleDropdownClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDropdownClose = () => {
    setAnchorEl(null);
  };

  const handlePrimeClick = () => {
    window.open(primeLink, '_blank');
    handleDropdownClose();
  };

  const handleProClick = () => {
    window.open(proLink, '_blank');
    handleDropdownClose();
  };

  const getUserRoleConfig = () => {
    if (userIsPro)
      return {
        textVariant: {
          navbar: {
            textRoleButton: 'Acessar RetornoPro',
          },
          userCard: {
            textRoleButton: 'Gerenciar assinatura',
          },
          subscriptionList: {
            textRoleButton: 'Gerenciar assinatura',
          },
        },
        gradient: false,
        handleClick: showProRedirectConfirmation
          ? () => setOpen(true)
          : () => window.open(DEFAULT_ROUTES.retornoProApp, '_blank'),
      };

    if (userIsPremium)
      return {
        textVariant: {
          navbar: {
            textRoleButton: 'Conhecer o Retorno Pro',
          },
          userCard: {
            textRoleButton: 'Conhecer o Retorno Pro',
          },
          subscriptionList: {
            textRoleButton: 'Mudar para Plano Anual',
          },
        },
        gradient: false,
        handleClick: () => window.open(proLink, '_blank'),
      };

    return {
      textVariant: {
        navbar: {
          textRoleButton: 'Conhecer Retorno Prime',
        },
        userCard: {
          textRoleButton: 'Conhecer Retorno Prime',
        },
      },
      gradient: true,
      textRoleButton: 'Conhecer Retorno Prime',
      handleClick: handleDropdownClick,
      showDropdown: true,
    };
  };

  const roleConfig = getUserRoleConfig();

  // Se o usuário não tem assinatura, mostra o dropdown
  if (!userIsPro && !userIsPremium) {
    return (
      <>
        {roleConfig.gradient ? (
          <GradientButton onClick={roleConfig.handleClick} {...props}>
            {roleConfig.textVariant[textVariant].textRoleButton}
          </GradientButton>
        ) : (
          <Button
            variant="contained"
            color="secondary"
            onClick={roleConfig.handleClick}
            {...props}
          >
            {roleConfig.textVariant[textVariant].textRoleButton}
          </Button>
        )}

        <Menu
          anchorEl={anchorEl}
          open={dropdownOpen}
          onClose={handleDropdownClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          PaperProps={{
            sx: {
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
            },
          }}
        >
          <MenuItem onClick={handlePrimeClick} sx={{ py: 1.5 }}>
            <div>
              <Typography fontWeight={600} color="primary">
                Conheça o Retorno Prime
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Para investidores individuais
              </Typography>
            </div>
          </MenuItem>
          <Divider sx={{ my: 0 }} />
          <MenuItem onClick={handleProClick} sx={{ py: 1.5 }}>
            <div>
              <Typography fontWeight={600} color="primary">
                Conheça o Retorno Pro
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Para profissionais do mercado
              </Typography>
            </div>
          </MenuItem>
        </Menu>

        <RedirectProDialog open={open} onClose={() => setOpen(false)} />
      </>
    );
  }

  // Para usuários com assinatura, mantém o comportamento atual
  return (
    <>
      {roleConfig.gradient ? (
        <GradientButton onClick={roleConfig.handleClick} {...props}>
          {roleConfig.textVariant[textVariant].textRoleButton}
        </GradientButton>
      ) : (
        <Button
          variant="contained"
          color="secondary"
          onClick={roleConfig.handleClick}
          {...props}
        >
          {roleConfig.textVariant[textVariant].textRoleButton}
        </Button>
      )}
      <RedirectProDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
};
